import { useState } from 'react';
import { ApiError } from '@/api/client';
import { createCarrier, updateCarrier } from './api';
import type { CarrierCompany } from './types';
import s from './AssemblyLineSheet.module.css';

const READONLY = import.meta.env.VITE_READONLY !== '0';

export function CarrierSheet({ carrier, onClose, onSaved }: { carrier?: CarrierCompany; onClose: () => void; onSaved: () => void }) {
  const [type, setType] = useState<CarrierCompany['type']>(carrier?.type || 'EXPRESS');
  const [name, setName] = useState(carrier?.name || '');
  const [code, setCode] = useState(carrier?.code || '');
  const [websiteUrl, setWebsiteUrl] = useState(carrier?.websiteUrl || '');
  const [trackingUrl, setTrackingUrl] = useState(carrier?.trackingUrl || '');
  const [active, setActive] = useState(carrier?.isActive ?? true);
  const [saving, setSaving] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState('');
  const save = async () => {
    if (READONLY || saving || uncertain) return;
    if (!name.trim()) return setError('请输入公司名称');
    setSaving(true); setError('');
    try {
      if (carrier) await updateCarrier(carrier.id, { name: name.trim(), code: code.trim(), websiteUrl: websiteUrl.trim(), trackingUrl: trackingUrl.trim(), isActive: active, expectedUpdatedAt: carrier.updatedAt });
      else await createCarrier({ type, name: name.trim(), code: code.trim(), websiteUrl: websiteUrl.trim(), trackingUrl: trackingUrl.trim() });
      onSaved();
    } catch (cause) {
      const unknown = !(cause instanceof ApiError) || cause.status === 0 || cause.status >= 500;
      setUncertain(unknown);
      setError(unknown ? '保存结果未确认。请关闭窗口核对列表后再操作。' : cause instanceof Error ? cause.message : '保存失败');
    } finally { setSaving(false); }
  };
  return <div className={s.backdrop} role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) onClose(); }}><section className={s.sheet} role="dialog" aria-modal="true" aria-label={carrier ? `编辑物流公司 ${carrier.name}` : '新增物流公司'}>
    <header><h2>{carrier ? `编辑 · ${carrier.name}` : '新增物流公司'}</h2><button type="button" aria-label="关闭" disabled={saving} onClick={onClose}>×</button></header>
    <div className={s.body}>
      <div className={s.fields}>
        <label>类型 <em>*</em><select value={type} disabled={!!carrier} onChange={(event) => setType(event.target.value as CarrierCompany['type'])}><option value="EXPRESS">快递</option><option value="LOGISTICS">物流</option></select></label>
        <label>公司名称 <em>*</em><input autoFocus maxLength={100} value={name} onChange={(event) => setName(event.target.value)} placeholder="例如：顺丰陆运" /></label>
        <label>编码<input maxLength={100} value={code} onChange={(event) => setCode(event.target.value)} placeholder="可选" /></label>
        <label>官网 HTTPS 地址<input type="url" maxLength={500} value={websiteUrl} onChange={(event) => setWebsiteUrl(event.target.value)} placeholder="https://example.com" /></label>
        <label>查单 HTTPS 地址<input type="url" maxLength={500} value={trackingUrl} onChange={(event) => setTrackingUrl(event.target.value)} placeholder="https://example.com/track" /></label>
        {carrier && <label>状态<select value={active ? 'active' : 'inactive'} onChange={(event) => setActive(event.target.value === 'active')}><option value="active">启用</option><option value="inactive">停用</option></select></label>}
      </div>
      <p className={s.hint}>停用后，V1 和 V2 的发货承运方下拉都不再显示该公司；历史发货记录保留原名称。</p>
      {carrier && !active && carrier.recentUse ? <div className={s.notice}><b>最近 30 天使用过 {carrier.recentUse} 次</b><p>停用只影响之后的选择，不会改历史发货记录。</p></div> : null}
      {error && <p role="alert" className={s.error}>{error}</p>}
      {READONLY && <p className={s.hint}>当前为只读预览，不能保存。</p>}
    </div>
    <footer><button type="button" disabled={saving} onClick={onClose}>取消</button><button type="button" className={s.primary} disabled={READONLY || saving || uncertain} onClick={() => void save()}>{saving ? '保存中…' : '保存'}</button></footer>
  </section></div>;
}
